import { SelectChangeEvent, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Controller, Path, useWatch } from 'react-hook-form';

import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import {
  getFieldErrorValue,
  getValidations,
} from '@/components/utils/inputUtils';
import { EstateWaterfallHypotheticalTransferTransferTaxKind } from '@/types/schema';

import { useTransferGiftTypeOptions } from './hooks/useTransferGiftTypeOptions';
import { HypotheticalTransferFormShape } from './HypotheticalTransferForm';

interface TransferTypeProps {
  waterfallId: string;
}

export function TransferKindInput({ waterfallId }: TransferTypeProps) {
  const { typeOptions: grantors, loading: isLoadingOptions } =
    useTransferGiftTypeOptions(waterfallId);

  const {
    control,
    formState: { isLoading: isLoadingForm },
    setValue,
  } = useFormContext<HypotheticalTransferFormShape>();

  const transferTaxKind = useWatch({
    control,
    name: 'transferTaxKind',
  });

  const isTaxable = useMemo(() => {
    return (
      transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift ||
      transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.NonGiftTransfer
    );
  }, [transferTaxKind]);

  const giftingGrantorIDs = useWatch({
    control,
    name: 'giftingGrantorIDs',
  });

  // Option values are either 'both', 'annualExclusionGift', 'charitableGift', 'genericNonTaxableGift', or a primaryClient id
  const options = useMemo(() => {
    return compact([
      {
        component: <SelectItemGroupLabel label="Taxable gift from" />,
        type: 'component' as const,
      },
      grantors.length === 2
        ? {
            value: 'both',
            display: `${grantors[0]?.display} and ${grantors[1]?.display}`,
          }
        : null,
      ...grantors.map((grantor) => ({
        value: grantor.value,
        display: grantor.display,
      })),
      {
        component: <SelectItemGroupLabel label="Non-taxable gifts" />,
        type: 'component' as const,
      },
      {
        display: 'Annual exclusion gift',
        value: 'annualExclusionGift',
      },
      {
        display: 'Charitable gift',
        value: 'charitableGift',
      },
      {
        display: 'Non-taxable transfer',
        value: 'genericNonTaxableGift',
      },
    ]);
  }, [grantors]);

  // Set the form state based on the selected value
  const handleGiftTypeChange = useCallback(
    (e: SelectChangeEvent<string | undefined>) => {
      const selectedValue = e.target.value;
      // Save the raw input to the form state so it passes
      // the required input validation
      setValue('transferKind', selectedValue);

      if (!selectedValue) {
        setValue('transferTaxKind', undefined);
        setValue('giftingGrantorIDs', undefined);
        return;
      }

      switch (selectedValue) {
        case 'both':
          setValue(
            'transferTaxKind',
            EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift
          );
          setValue('isGstGift', true);
          setValue('giftingGrantorIDs', [
            grantors[0]?.value ?? '',
            grantors[1]?.value ?? '',
          ]);
          return;

        case 'annualExclusionGift':
          setValue(
            'transferTaxKind',
            EstateWaterfallHypotheticalTransferTransferTaxKind.AnualExclusionGift
          );
          setValue('isGstGift', false);
          setValue('giftingGrantorIDs', undefined);
          return;

        case 'charitableGift':
          setValue(
            'transferTaxKind',
            EstateWaterfallHypotheticalTransferTransferTaxKind.CharitableGift
          );
          setValue('isGstGift', false);
          setValue('giftingGrantorIDs', undefined);
          return;

        case 'genericNonTaxableGift':
          setValue(
            'transferTaxKind',
            EstateWaterfallHypotheticalTransferTransferTaxKind.GenericNonTaxableGift
          );
          setValue('isGstGift', false);
          setValue('giftingGrantorIDs', undefined);
          return;

        default:
          setValue(
            'transferTaxKind',
            EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift
          );
          setValue('isGstGift', true);
          setValue('giftingGrantorIDs', [selectedValue]);
      }
    },
    [grantors, setValue]
  );

  // Derive the selected value from the form state
  const value = useMemo(() => {
    if (isTaxable && giftingGrantorIDs) {
      if (giftingGrantorIDs.length === 2) {
        setValue('transferKind', 'both');
        return 'both';
      }

      setValue('transferKind', giftingGrantorIDs[0]);
      return giftingGrantorIDs[0];
    } else {
      if (
        transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.AnualExclusionGift
      ) {
        setValue('transferKind', 'annualExclusionGift');
        return 'annualExclusionGift';
      }

      if (
        transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.CharitableGift
      ) {
        setValue('transferKind', 'charitableGift');
        return 'charitableGift';
      }

      if (
        transferTaxKind ===
        EstateWaterfallHypotheticalTransferTransferTaxKind.GenericNonTaxableGift
      ) {
        setValue('transferKind', 'genericNonTaxableGift');
        return 'genericNonTaxableGift';
      }

      setValue('transferKind', undefined);
      return '';
    }
  }, [giftingGrantorIDs, isTaxable, setValue, transferTaxKind]);

  const helpText = useMemo(() => {
    if (!value) {
      return '';
    }

    if (isTaxable) {
      const giftGrantor = options.find((o) => o.value === value);
      return `Taxable gift from ${giftGrantor?.display ?? 'unknown'}`;
    }

    return 'Non-taxable gift';
  }, [isTaxable, options, value]);

  const label = 'Type of transfer (gift tax purposes)';
  const validations = getValidations(label, true);

  // Because this field controls a number of fields on the form state
  // through setValue, we need to control it independently so we get
  // the correct validation errors
  return (
    <FormLayoutRow>
      <FormLayoutItem width={isTaxable ? 8 : 12}>
        <Controller
          name="transferKind"
          control={control}
          defaultValue={undefined}
          rules={{
            validate: validations,
          }}
          render={({ fieldState, formState }) => (
            <SelectInput
              label={label}
              disabled={isLoadingForm || isLoadingOptions}
              options={options}
              onChange={(e) => handleGiftTypeChange(e)}
              value={value}
              required
              helpText={helpText}
              errorMessage={getFieldErrorValue(
                fieldState,
                formState.isSubmitted
              )}
            />
          )}
        />
      </FormLayoutItem>
      {isTaxable && (
        <FormLayoutItem width={4}>
          <Stack height="100%" justifyContent="center">
            <FormAwareCheckbox<HypotheticalTransferFormShape>
              control={control}
              fieldName={
                'isGstGift' as const satisfies Path<HypotheticalTransferFormShape>
              }
              label="GST Gift"
            />
          </Stack>
        </FormLayoutItem>
      )}
    </FormLayoutRow>
  );
}
